import { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import { Loader2, Save, Bot, Globe, Bell, Inbox, Sunrise, CalendarCheck } from 'lucide-react';
import { useWorkspace } from '@/contexts/context';
import { supabase } from '@saas-boilerplate/shared';
import { invokeEdgeFn } from '@/lib/edge-functions';

/**
 * Auto-join meetings (PRD_AUTO_ENROL Phase 2): per-user rules for auto-enrolling calendar meetings
 * with the bot. Enabling triggers an immediate 14-day backfill; thereafter the calendar delta
 * stream + 6-hourly reconcile keep it current.
 */
function AutoJoinCard() {
  const { workspace, userProfile } = useWorkspace();
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [scope, setScope] = useState<'external_only' | 'all'>('all');
  const [skipAllDay, setSkipAllDay] = useState(true);
  const [skipSolo, setSkipSolo] = useState(true);
  const [keywords, setKeywords] = useState('');

  useEffect(() => {
    if (!workspace?.id || !userProfile?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('auto_enrol_settings')
        .select('*')
        .eq('workspace_id', workspace.id)
        .eq('user_id', userProfile.id)
        .maybeSingle();
      if (cancelled) return;
      if (data) {
        setEnabled(Boolean(data.enabled));
        setScope((data.scope as 'external_only' | 'all') || 'external_only');
        setSkipAllDay(Boolean(data.skip_all_day));
        setSkipSolo(Boolean(data.skip_solo));
        setKeywords(((data.title_exclude_keywords as string[]) || []).join(', '));
      }
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [workspace?.id, userProfile?.id]);

  const save = async () => {
    if (!workspace?.id || !userProfile?.id) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('auto_enrol_settings').upsert(
        {
          workspace_id: workspace.id,
          user_id: userProfile.id,
          enabled,
          scope,
          skip_all_day: skipAllDay,
          skip_solo: skipSolo,
          title_exclude_keywords: keywords
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'workspace_id,user_id' },
      );
      if (error) throw error;
      if (enabled) {
        // Backfill the next 14 days now - webhooks only see future changes.
        const res = await invokeEdgeFn<{ created?: number; evaluated?: number; no_link?: number }>(
          'calendar-watch-manage',
          {
            action: 'auto_enrol_backfill',
            workspace_id: workspace.id,
          },
        );
        const created = res?.created || 0;
        const noLink = res?.no_link || 0;
        // Explain a low/zero count: the most common reason an eligible event is skipped is that it
        // has no video link for the notetaker to join (interviews, in-person events, phone screens).
        const noLinkNote = noLink > 0 ? ` ${noLink} event${noLink === 1 ? '' : 's'} had no meeting link to join.` : '';
        toast.success(
          `Auto-join saved - ${created} meeting${created === 1 ? '' : 's'} enrolled from your next 14 days.${noLinkNote}`,
        );
      } else {
        toast.success('Auto-join saved.');
      }
    } catch (e: any) {
      toast.error('Failed to save auto-join settings', { description: e?.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarCheck className="h-5 w-5" />
          Auto-join meetings
        </CardTitle>
        <CardDescription>
          Automatically enrol your upcoming calendar meetings and send the notetaker - no per-meeting
          action. You can exclude any meeting before it's joined.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!loaded ? (
          <div className="flex h-16 items-center justify-center">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label>Enable auto-join</Label>
                <p className="text-xs text-muted-foreground">
                  Applies to meetings on your connected calendar in this workspace.
                </p>
              </div>
              <Switch checked={enabled} onCheckedChange={setEnabled} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Which meetings</Label>
                <Select value={scope} onValueChange={(v) => setScope(v as 'external_only' | 'all')} disabled={!enabled}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="external_only">With external attendees only</SelectItem>
                    <SelectItem value="all">All meetings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="auto-join-keywords">Skip titles containing</Label>
                <Input
                  id="auto-join-keywords"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  placeholder="e.g. interview, 1:1, private"
                  disabled={!enabled}
                  className="h-9"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={skipAllDay} onCheckedChange={setSkipAllDay} disabled={!enabled} />
                Skip all-day events
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={skipSolo} onCheckedChange={setSkipSolo} disabled={!enabled} />
                Skip events with no other attendees
              </label>
            </div>
            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} size="sm">
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

type DateFormat = 'mdy' | 'dmy' | 'ymd' | 'iso';
type TimeFormat = '12h' | '24h';
type ChannelPref = 'email' | 'slack' | 'both';
type RecapTiming = 'immediate' | 'delay_15m' | 'delay_1h' | 'delay_eod' | 'on_approval' | 'off';
type ApprovalTimeout = 'auto_send_24h' | 'hold_indefinitely';
type SlackRouting = 'dm' | 'channel_dm' | 'channel_only';
type BriefingChannel = 'email' | 'slack';

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'ja', label: '日本語' },
];

const CURRENCY_OPTIONS = [
  { value: 'usd', label: 'USD - US dollar' },
  { value: 'eur', label: 'EUR - Euro' },
  { value: 'gbp', label: 'GBP - Pound sterling' },
  { value: 'jpy', label: 'JPY - Japanese yen' },
  { value: 'cad', label: 'CAD - Canadian dollar' },
];

const DATE_FORMAT_OPTIONS: { value: DateFormat; label: string }[] = [
  { value: 'mdy', label: 'MM/DD/YYYY' },
  { value: 'dmy', label: 'DD/MM/YYYY' },
  { value: 'ymd', label: 'YYYY/MM/DD' },
  { value: 'iso', label: 'YYYY-MM-DD (ISO)' },
];

const TIME_FORMAT_OPTIONS: { value: TimeFormat; label: string }[] = [
  { value: '12h', label: '12-hour (1:30 PM)' },
  { value: '24h', label: '24-hour (13:30)' },
];

const RECAP_TIMING_OPTIONS: { value: RecapTiming; label: string }[] = [
  { value: 'immediate', label: 'Immediate (within 2 min of meeting end)' },
  { value: 'delay_15m', label: 'Delayed by 15 minutes' },
  { value: 'delay_1h', label: 'Delayed by 1 hour' },
  { value: 'delay_eod', label: 'End of day' },
  { value: 'on_approval', label: 'Wait for my approval' },
  { value: 'off', label: 'Off (never send)' },
];

const PRE_NUDGE_PRESETS: { value: number | null; label: string }[] = [
  { value: null, label: 'Off' },
  { value: 30, label: '30 minutes before' },
  { value: 120, label: '2 hours before' },
  { value: 1440, label: '24 hours before' },
  { value: 2880, label: '2 days before' },
  { value: 10080, label: '7 days before' },
];

const INHERIT = '__inherit__';

function getTimezoneOptions(): string[] {
  const anyIntl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
  if (typeof anyIntl.supportedValuesOf === 'function') {
    try {
      return anyIntl.supportedValuesOf('timeZone');
    } catch {
      // fall through
    }
  }
  return [
    'UTC',
    'America/Los_Angeles',
    'America/Denver',
    'America/Chicago',
    'America/New_York',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Asia/Singapore',
    'Australia/Sydney',
  ];
}

type PersonalNotificationRow = {
  channel_preference: ChannelPref;
  recap_timing: RecapTiming;
  approval_required: boolean;
  approval_timeout: ApprovalTimeout;
  pre_meeting_nudge_minutes: number | null;
  pre_meeting_nudge_for_declined: boolean;
  slack_channel_routing: SlackRouting;
  morning_briefing_enabled: boolean;
  morning_briefing_time: string; // 'HH:MM' or 'HH:MM:SS'
  morning_briefing_weekends: boolean;
  morning_briefing_channel: BriefingChannel | null;
  channel_notification_suppression: boolean;
  marketing_cta_visible_to_me: boolean;
};

type RecipientPrefRow = {
  email_unsubscribed_all: boolean;
  slack_muted_all: boolean;
  email_type_optouts: string[];
  slack_type_optouts: string[];
};

const DEFAULT_NOTIF: PersonalNotificationRow = {
  channel_preference: 'both',
  recap_timing: 'immediate',
  approval_required: false,
  approval_timeout: 'auto_send_24h',
  pre_meeting_nudge_minutes: 120,
  pre_meeting_nudge_for_declined: true,
  slack_channel_routing: 'dm',
  morning_briefing_enabled: false,
  morning_briefing_time: '07:30',
  morning_briefing_weekends: false,
  morning_briefing_channel: null,
  channel_notification_suppression: false,
  marketing_cta_visible_to_me: false,
};

const DEFAULT_RECIPIENT: RecipientPrefRow = {
  email_unsubscribed_all: false,
  slack_muted_all: false,
  email_type_optouts: [],
  slack_type_optouts: [],
};

function normalizeTime(raw: string): string {
  // Accept 'HH:MM' or 'HH:MM:SS' and emit 'HH:MM'
  const m = /^(\d{2}):(\d{2})/.exec(raw || '');
  return m ? `${m[1]}:${m[2]}` : '07:30';
}

/** A working / notification window, stored as JSON inside workspace_members.regional_overrides. */
type DayWindow = { start: string; end: string; include_weekends: boolean };
const DEFAULT_WORKING_HOURS: DayWindow = { start: '08:00', end: '18:00', include_weekends: false };
const DEFAULT_NOTIFICATION_HOURS: DayWindow = { start: '07:00', end: '19:00', include_weekends: false };

/** Shift a 'HH:MM' time by ±hours, wrapping at midnight. Used to derive default notification
 *  hours from working hours (working ± 1h). */
function shiftTime(time: string, hours: number): string {
  const m = /^(\d{2}):(\d{2})$/.exec(time);
  if (!m) return time;
  const minutes = Number(m[1]) * 60 + Number(m[2]) + hours * 60;
  const clamped = ((minutes % 1440) + 1440) % 1440;
  return `${String(Math.floor(clamped / 60)).padStart(2, '0')}:${String(clamped % 60).padStart(2, '0')}`;
}

export function PersonalSettings({ section = 'personal' }: { section?: 'personal' | 'notifications' }) {
  const { workspace, userProfile, refreshWorkspace } = useWorkspace();
  const [botName, setBotName] = useState('');
  const [timezone, setTimezone] = useState<string>(INHERIT);
  const [language, setLanguage] = useState<string>(INHERIT);
  const [currency, setCurrency] = useState<string>(INHERIT);
  const [dateFormat, setDateFormat] = useState<string>(INHERIT);
  const [timeFormat, setTimeFormat] = useState<string>(INHERIT);
  const [workingHours, setWorkingHours] = useState<DayWindow>(DEFAULT_WORKING_HOURS);
  const [notificationHours, setNotificationHours] = useState<DayWindow>(DEFAULT_NOTIFICATION_HOURS);
  const [notif, setNotif] = useState<PersonalNotificationRow>(DEFAULT_NOTIF);
  const [recipient, setRecipient] = useState<RecipientPrefRow>(DEFAULT_RECIPIENT);
  const [loaded, setLoaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const lastKeyRef = useRef<string | null>(null);

  const timezoneOptions = useMemo(getTimezoneOptions, []);

  useEffect(() => {
    if (!workspace?.id || !userProfile?.id) return;
    const key = `${workspace.id}:${userProfile.id}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;
    (async () => {
      const [memberRes, notifRes, recipRes] = await Promise.all([
        supabase
          .from('workspace_members')
          .select('bot_name, regional_overrides')
          .eq('workspace_id', workspace.id)
          .eq('user_id', userProfile.id)
          .maybeSingle(),
        supabase
          .from('personal_notification_settings')
          .select('*')
          .eq('workspace_id', workspace.id)
          .eq('user_id', userProfile.id)
          .maybeSingle(),
        supabase
          .from('recipient_preferences')
          .select('*')
          .eq('workspace_id', workspace.id)
          .eq('user_id', userProfile.id)
          .maybeSingle(),
      ]);

      const memberRow = (memberRes.data || null) as {
        bot_name?: string | null;
        regional_overrides?: Record<string, unknown> | null;
      } | null;
      setBotName(memberRow?.bot_name || '');
      const ov = memberRow?.regional_overrides || {};
      setTimezone(typeof ov.timezone === 'string' ? ov.timezone : INHERIT);
      setLanguage(typeof ov.language === 'string' ? ov.language : INHERIT);
      setCurrency(typeof ov.currency === 'string' ? ov.currency : INHERIT);
      setDateFormat(typeof ov.date_format === 'string' ? ov.date_format : INHERIT);
      setTimeFormat(typeof ov.time_format === 'string' ? ov.time_format : INHERIT);
      const wh = (ov.working_hours as Partial<DayWindow> | undefined) || null;
      if (wh && wh.start && wh.end) {
        setWorkingHours({
          start: wh.start,
          end: wh.end,
          include_weekends: Boolean(wh.include_weekends),
        });
      }
      const nh = (ov.notification_hours as Partial<DayWindow> | undefined) || null;
      if (nh && nh.start && nh.end) {
        setNotificationHours({
          start: nh.start,
          end: nh.end,
          include_weekends: Boolean(nh.include_weekends),
        });
      }

      if (notifRes.data) {
        const n = notifRes.data as PersonalNotificationRow;
        setNotif({
          ...n,
          morning_briefing_time: normalizeTime(n.morning_briefing_time),
        });
      }
      if (recipRes.data) {
        setRecipient(recipRes.data as RecipientPrefRow);
      }

      setLoaded(true);
    })();
  }, [workspace?.id, userProfile?.id]);

  const handleSave = async () => {
    if (!workspace?.id || !userProfile?.id) return;
    setIsSaving(true);
    try {
      const regional: Record<string, unknown> = {};
      if (timezone !== INHERIT) regional.timezone = timezone;
      if (language !== INHERIT) regional.language = language;
      if (currency !== INHERIT) regional.currency = currency;
      if (dateFormat !== INHERIT) regional.date_format = dateFormat;
      if (timeFormat !== INHERIT) regional.time_format = timeFormat;
      regional.working_hours = workingHours;
      regional.notification_hours = notificationHours;

      const memberUpdate = supabase
        .from('workspace_members')
        .update({ bot_name: botName.trim() || null, regional_overrides: regional })
        .eq('workspace_id', workspace.id)
        .eq('user_id', userProfile.id);

      const notifUpsert = supabase
        .from('personal_notification_settings')
        .upsert(
          {
            workspace_id: workspace.id,
            user_id: userProfile.id,
            ...notif,
            morning_briefing_time: normalizeTime(notif.morning_briefing_time),
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'workspace_id,user_id' },
        );

      const recipientUpsert = supabase
        .from('recipient_preferences')
        .upsert(
          {
            workspace_id: workspace.id,
            user_id: userProfile.id,
            ...recipient,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'workspace_id,user_id' },
        );

      const [m, n, r] = await Promise.all([memberUpdate, notifUpsert, recipientUpsert]);
      if (m.error) throw m.error;
      if (n.error) throw n.error;
      if (r.error) throw r.error;
      await refreshWorkspace({ silent: true });
      toast.success('Personal settings saved');
    } catch (e: any) {
      toast.error('Failed to save', { description: e?.message });
    } finally {
      setIsSaving(false);
    }
  };

  const workspaceFallback = ((workspace as any)?.bot_name as string | null) || 'Upstand Assistant';
  const wsSettings = ((workspace as any)?.settings as Record<string, string> | null) || {};
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const tzWorkspaceDefault = wsSettings.timezone || browserTz;

  const wsDateFormatLabel = wsSettings.date_format
    ? (DATE_FORMAT_OPTIONS.find((o) => o.value === wsSettings.date_format)?.label ?? wsSettings.date_format)
    : null;
  const wsTimeFormatLabel = wsSettings.time_format
    ? (TIME_FORMAT_OPTIONS.find((o) => o.value === wsSettings.time_format)?.label ?? wsSettings.time_format)
    : null;
  const wsLanguageLabel = wsSettings.language
    ? (LANGUAGE_OPTIONS.find((o) => o.value === wsSettings.language)?.label ?? wsSettings.language)
    : null;
  const wsCurrencyLabel = wsSettings.currency
    ? (CURRENCY_OPTIONS.find((o) => o.value === wsSettings.currency)?.label ?? wsSettings.currency)
    : null;

  const toggleTypeOptout = (
    channel: 'email' | 'slack',
    type: 'recap' | 'pre_nudge' | 'morning_briefing',
    on: boolean,
  ) => {
    setRecipient((prev) => {
      const field = channel === 'email' ? 'email_type_optouts' : 'slack_type_optouts';
      const arr = new Set(prev[field]);
      if (on) arr.add(type);
      else arr.delete(type);
      return { ...prev, [field]: [...arr] };
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">
          {section === 'notifications' ? 'Notifications' : 'Personal settings'}
        </h2>
        <p className="text-sm text-muted-foreground">
          {section === 'notifications'
            ? `How and when upstand.ai notifies you in the ${workspace?.name || 'workspace'} workspace.`
            : `Settings that apply to you across the ${workspace?.name || 'workspace'} workspace.`}
        </p>
      </div>

      {section === 'personal' && (
      <>
      {/* ───── Auto-join meetings ───── */}
      <AutoJoinCard />

      {/* ───── Identity & locale ───── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            AI bot
          </CardTitle>
          <CardDescription>
            The display name your AI assistant uses when joining meetings you own. Leave blank to
            use the team default ({workspaceFallback}).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="personal-bot-name">Bot name</Label>
            <Input
              id="personal-bot-name"
              value={botName}
              onChange={(e) => setBotName(e.target.value)}
              placeholder={workspaceFallback}
              maxLength={80}
              disabled={!loaded || isSaving}
            />
            <p className="text-xs text-muted-foreground">
              Used when you are the owner of the meeting series. Up to 80 characters.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Regional preferences
          </CardTitle>
          <CardDescription>
            These override the workspace defaults for how dates, times, and numbers are shown to
            you. Choose “Use workspace default” to inherit.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="personal-timezone">Timezone</Label>
            <Select value={timezone} onValueChange={setTimezone} disabled={!loaded || isSaving}>
              <SelectTrigger id="personal-timezone">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value={INHERIT}>
                  Use workspace default ({tzWorkspaceDefault})
                </SelectItem>
                {timezoneOptions.map((tz) => (
                  <SelectItem key={tz} value={tz}>
                    {tz}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Meeting times across the app will display in this timezone.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="personal-date-format">Date format</Label>
              <Select value={dateFormat} onValueChange={setDateFormat} disabled={!loaded || isSaving}>
                <SelectTrigger id="personal-date-format">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={INHERIT}>
                    {wsDateFormatLabel ? `Use workspace default (${wsDateFormatLabel})` : 'Use workspace default'}
                  </SelectItem>
                  {DATE_FORMAT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="personal-time-format">Time format</Label>
              <Select value={timeFormat} onValueChange={setTimeFormat} disabled={!loaded || isSaving}>
                <SelectTrigger id="personal-time-format">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={INHERIT}>
                    {wsTimeFormatLabel ? `Use workspace default (${wsTimeFormatLabel})` : 'Use workspace default'}
                  </SelectItem>
                  {TIME_FORMAT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="personal-language">Language</Label>
              <Select value={language} onValueChange={setLanguage} disabled={!loaded || isSaving}>
                <SelectTrigger id="personal-language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={INHERIT}>
                    {wsLanguageLabel ? `Use workspace default (${wsLanguageLabel})` : 'Use workspace default'}
                  </SelectItem>
                  {LANGUAGE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="personal-currency">Currency</Label>
              <Select value={currency} onValueChange={setCurrency} disabled={!loaded || isSaving}>
                <SelectTrigger id="personal-currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={INHERIT}>
                    {wsCurrencyLabel ? `Use workspace default (${wsCurrencyLabel})` : 'Use workspace default'}
                  </SelectItem>
                  {CURRENCY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ───── Working hours & notification hours ───── */}
      <Card>
        <CardHeader>
          <CardTitle>Working hours &amp; notifications</CardTitle>
          <CardDescription>
            The dashboard timeline shows your working hours. Notification hours bound when emails
            and Slack messages can land - pre-meeting nudges fire on schedule regardless (so a
            6 am nudge for a 7 am meeting still arrives in time).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="text-sm font-semibold">Working hours</Label>
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
              <div className="space-y-1">
                <Label htmlFor="wh-start" className="text-xs text-muted-foreground">Start</Label>
                <Input
                  id="wh-start"
                  type="time"
                  value={workingHours.start}
                  disabled={!loaded || isSaving}
                  onChange={(e) => setWorkingHours({ ...workingHours, start: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="wh-end" className="text-xs text-muted-foreground">End</Label>
                <Input
                  id="wh-end"
                  type="time"
                  value={workingHours.end}
                  disabled={!loaded || isSaving}
                  onChange={(e) => setWorkingHours({ ...workingHours, end: e.target.value })}
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground sm:pb-2">
                <input
                  type="checkbox"
                  checked={workingHours.include_weekends}
                  disabled={!loaded || isSaving}
                  onChange={(e) => setWorkingHours({ ...workingHours, include_weekends: e.target.checked })}
                  className="rounded border"
                />
                Include weekends
              </label>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Shows on the dashboard's Today graph and defines your work day.
            </p>
          </div>

          <div>
            <div className="flex items-baseline justify-between gap-3">
              <Label className="text-sm font-semibold">Notification hours</Label>
              <button
                type="button"
                className="text-xs font-medium text-primary hover:underline"
                disabled={!loaded || isSaving}
                onClick={() =>
                  setNotificationHours({
                    start: shiftTime(workingHours.start, -1),
                    end: shiftTime(workingHours.end, 1),
                    include_weekends: workingHours.include_weekends,
                  })
                }
              >
                Reset to working hours ± 1h
              </button>
            </div>
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
              <div className="space-y-1">
                <Label htmlFor="nh-start" className="text-xs text-muted-foreground">Start</Label>
                <Input
                  id="nh-start"
                  type="time"
                  value={notificationHours.start}
                  disabled={!loaded || isSaving}
                  onChange={(e) => setNotificationHours({ ...notificationHours, start: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="nh-end" className="text-xs text-muted-foreground">End</Label>
                <Input
                  id="nh-end"
                  type="time"
                  value={notificationHours.end}
                  disabled={!loaded || isSaving}
                  onChange={(e) => setNotificationHours({ ...notificationHours, end: e.target.value })}
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground sm:pb-2">
                <input
                  type="checkbox"
                  checked={notificationHours.include_weekends}
                  disabled={!loaded || isSaving}
                  onChange={(e) => setNotificationHours({ ...notificationHours, include_weekends: e.target.checked })}
                  className="rounded border"
                />
                Include weekends
              </label>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Morning briefings + recaps land inside this window. Pre-meeting nudges always fire
              on schedule regardless.
            </p>
          </div>
        </CardContent>
      </Card>
      </>
      )}

      {section === 'notifications' && (
      <>
      {/* ───── Defaults for meetings I create ───── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Defaults for meetings I create
          </CardTitle>
          <CardDescription>
            These are copied onto every new meeting you create. Changing them later does not affect
            existing meetings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Delivery channel</Label>
              <Select
                value={notif.channel_preference}
                onValueChange={(v) =>
                  setNotif((p) => ({ ...p, channel_preference: v as ChannelPref }))
                }
                disabled={!loaded || isSaving}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Email and Slack</SelectItem>
                  <SelectItem value="email">Email only</SelectItem>
                  <SelectItem value="slack">Slack only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Slack routing</Label>
              <Select
                value={notif.slack_channel_routing}
                onValueChange={(v) =>
                  setNotif((p) => ({ ...p, slack_channel_routing: v as SlackRouting }))
                }
                disabled={!loaded || isSaving}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dm">Direct messages only</SelectItem>
                  <SelectItem value="channel_dm">Channel + DMs</SelectItem>
                  <SelectItem value="channel_only">Channel only</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                The actual Slack channel is chosen when each meeting is created.
              </p>
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label>Pre-meeting nudge timing</Label>
            <Select
              value={notif.pre_meeting_nudge_minutes === null
                ? 'off'
                : String(notif.pre_meeting_nudge_minutes)}
              onValueChange={(v) =>
                setNotif((p) => ({
                  ...p,
                  pre_meeting_nudge_minutes: v === 'off' ? null : Number(v),
                }))
              }
              disabled={!loaded || isSaving}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRE_NUDGE_PRESETS.map((o) => (
                  <SelectItem key={o.value ?? 'off'} value={o.value === null ? 'off' : String(o.value)}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-start justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="nudge-declined">Send async-update request to declined attendees</Label>
              <p className="text-xs text-muted-foreground">
                Invite anyone who declined to submit a written or voice update before the meeting.
              </p>
            </div>
            <Switch
              id="nudge-declined"
              checked={notif.pre_meeting_nudge_for_declined}
              onCheckedChange={(v) =>
                setNotif((p) => ({ ...p, pre_meeting_nudge_for_declined: v }))
              }
              disabled={!loaded || isSaving}
              className="mt-0.5"
            />
          </div>

          <Separator />

          <div className="space-y-2">
            <Label>Post-meeting recap timing</Label>
            <Select
              value={notif.recap_timing}
              onValueChange={(v) => setNotif((p) => ({ ...p, recap_timing: v as RecapTiming }))}
              disabled={!loaded || isSaving}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECAP_TIMING_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-start justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="approval-required">Require my approval before sending</Label>
              <p className="text-xs text-muted-foreground">
                You will receive a preview and must approve before recipients see the recap.
              </p>
            </div>
            <Switch
              id="approval-required"
              checked={notif.approval_required}
              onCheckedChange={(v) => setNotif((p) => ({ ...p, approval_required: v }))}
              disabled={!loaded || isSaving}
              className="mt-0.5"
            />
          </div>

          {notif.approval_required && (
            <div className="space-y-2">
              <Label>If I don’t respond within 24 hours</Label>
              <Select
                value={notif.approval_timeout}
                onValueChange={(v) =>
                  setNotif((p) => ({ ...p, approval_timeout: v as ApprovalTimeout }))
                }
                disabled={!loaded || isSaving}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto_send_24h">
                    Auto-send with “owner did not review” note
                  </SelectItem>
                  <SelectItem value="hold_indefinitely">Hold indefinitely</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ───── Morning briefing ───── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sunrise className="h-5 w-5" />
            Morning briefing
          </CardTitle>
          <CardDescription>
            An optional daily heads-up summarising every meeting on your day, newly added agenda
            items, and outstanding actions assigned to you.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="briefing-enabled">Send me the morning briefing</Label>
              <p className="text-xs text-muted-foreground">
                Delivered weekdays only by default. Recipients are existing upstand.ai users only.
              </p>
            </div>
            <Switch
              id="briefing-enabled"
              checked={notif.morning_briefing_enabled}
              onCheckedChange={(v) =>
                setNotif((p) => ({ ...p, morning_briefing_enabled: v }))
              }
              disabled={!loaded || isSaving}
              className="mt-0.5"
            />
          </div>

          {notif.morning_briefing_enabled && (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="briefing-time">Delivery time</Label>
                  <Input
                    id="briefing-time"
                    type="time"
                    value={notif.morning_briefing_time}
                    onChange={(e) =>
                      setNotif((p) => ({ ...p, morning_briefing_time: e.target.value }))
                    }
                    disabled={!loaded || isSaving}
                  />
                  <p className="text-xs text-muted-foreground">In your local timezone. 5am–11am.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="briefing-channel">Delivery channel</Label>
                  <Select
                    value={notif.morning_briefing_channel || INHERIT}
                    onValueChange={(v) =>
                      setNotif((p) => ({
                        ...p,
                        morning_briefing_channel: v === INHERIT ? null : (v as BriefingChannel),
                      }))
                    }
                    disabled={!loaded || isSaving}
                  >
                    <SelectTrigger id="briefing-channel">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={INHERIT}>Same as my channel preference</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="slack">Slack DM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-start justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="briefing-weekends">Also send on weekends</Label>
                  <p className="text-xs text-muted-foreground">
                    Default is weekdays only.
                  </p>
                </div>
                <Switch
                  id="briefing-weekends"
                  checked={notif.morning_briefing_weekends}
                  onCheckedChange={(v) =>
                    setNotif((p) => ({ ...p, morning_briefing_weekends: v }))
                  }
                  disabled={!loaded || isSaving}
                  className="mt-0.5"
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ───── What I receive (recipient preferences) ───── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Inbox className="h-5 w-5" />
            What I receive
          </CardTitle>
          <CardDescription>
            These control what arrives in your own inbox and Slack DMs, regardless of who organised
            the meeting. Channel-level unsubscribes always take effect immediately.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Per message type
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2 rounded-md border border-border/60 p-3">
                <p className="text-sm font-medium">Post-meeting recaps</p>
                <label className="flex items-center justify-between text-sm">
                  <span>Email</span>
                  <Switch
                    checked={!recipient.email_type_optouts.includes('recap')}
                    onCheckedChange={(v) => toggleTypeOptout('email', 'recap', !v)}
                    disabled={!loaded || isSaving}
                  />
                </label>
                <label className="flex items-center justify-between text-sm">
                  <span>Slack</span>
                  <Switch
                    checked={!recipient.slack_type_optouts.includes('recap')}
                    onCheckedChange={(v) => toggleTypeOptout('slack', 'recap', !v)}
                    disabled={!loaded || isSaving}
                  />
                </label>
              </div>
              <div className="space-y-2 rounded-md border border-border/60 p-3">
                <p className="text-sm font-medium">Pre-meeting nudges</p>
                <label className="flex items-center justify-between text-sm">
                  <span>Email</span>
                  <Switch
                    checked={!recipient.email_type_optouts.includes('pre_nudge')}
                    onCheckedChange={(v) => toggleTypeOptout('email', 'pre_nudge', !v)}
                    disabled={!loaded || isSaving}
                  />
                </label>
                <label className="flex items-center justify-between text-sm">
                  <span>Slack</span>
                  <Switch
                    checked={!recipient.slack_type_optouts.includes('pre_nudge')}
                    onCheckedChange={(v) => toggleTypeOptout('slack', 'pre_nudge', !v)}
                    disabled={!loaded || isSaving}
                  />
                </label>
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Channel-level mutes
            </p>
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-0.5">
                <Label htmlFor="email-unsub">Unsubscribe from all upstand.ai emails</Label>
                <p className="text-xs text-muted-foreground">
                  Strongest opt-out. You will still see recap and meeting content when you sign in
                  to upstand.ai.
                </p>
              </div>
              <Switch
                id="email-unsub"
                checked={recipient.email_unsubscribed_all}
                onCheckedChange={(v) =>
                  setRecipient((p) => ({ ...p, email_unsubscribed_all: v }))
                }
                disabled={!loaded || isSaving}
                className="mt-0.5"
              />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-0.5">
                <Label htmlFor="slack-mute">Mute the upstand.ai Slack app</Label>
                <p className="text-xs text-muted-foreground">
                  All Slack DMs from upstand.ai will be suppressed.
                </p>
              </div>
              <Switch
                id="slack-mute"
                checked={recipient.slack_muted_all}
                onCheckedChange={(v) => setRecipient((p) => ({ ...p, slack_muted_all: v }))}
                disabled={!loaded || isSaving}
                className="mt-0.5"
              />
            </div>
          </div>

          <Separator />

          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Other
            </p>
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-0.5">
                <Label htmlFor="channel-suppression">
                  Suppress channel posts for meetings I attended
                </Label>
                <p className="text-xs text-muted-foreground">
                  Don’t notify me about a channel post for a meeting I was already in. DMs still
                  arrive.
                </p>
              </div>
              <Switch
                id="channel-suppression"
                checked={notif.channel_notification_suppression}
                onCheckedChange={(v) =>
                  setNotif((p) => ({ ...p, channel_notification_suppression: v }))
                }
                disabled={!loaded || isSaving}
                className="mt-0.5"
              />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-0.5">
                <Label htmlFor="cta-visible">Show the marketing CTA on messages addressed to me</Label>
                <p className="text-xs text-muted-foreground">
                  Off by default for existing upstand.ai users. Rarely useful.
                </p>
              </div>
              <Switch
                id="cta-visible"
                checked={notif.marketing_cta_visible_to_me}
                onCheckedChange={(v) =>
                  setNotif((p) => ({ ...p, marketing_cta_visible_to_me: v }))
                }
                disabled={!loaded || isSaving}
                className="mt-0.5"
              />
            </div>
          </div>
        </CardContent>
      </Card>
      </>
      )}

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={!loaded || isSaving}>
          {isSaving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save {section === 'notifications' ? 'notification' : 'personal'} settings
        </Button>
      </div>
    </div>
  );
}
